import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 relative flex items-center justify-center">
          <Button variant="ghost" size="icon" className="shrink-0 absolute left-4" asChild>
            <Link to="/">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-7 object-contain"
            />
            <span className="text-primary font-sans font-bold text-xl tracking-tight">Dibs</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: April 7, 2026</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-10 text-[15px] leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Agreement to Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using the Dibs platform and services (the "Service") operated by <strong className="text-foreground">Dibs Everything Pte Ltd</strong> (UEN: 202418462H) ("we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
            </p>
            <p className="text-muted-foreground mt-3">
              These Terms apply to all users, including:
            </p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">Business Users</strong> — business owners and administrators who register for an account to manage bookings, listings, and pricing.</li>
              <li><strong className="text-foreground">End-User Customers</strong> — individuals who make bookings through Dibs-powered booking pages.</li>
            </ul>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">2. Description of the Service</h2>
            <p className="text-muted-foreground">
              Dibs is a dynamic pricing booking system for service-based businesses. The Service enables Business Users to:
            </p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>Create and manage service listings with configurable schedules and capacity</li>
              <li>Set base prices and configure dynamic pricing rules based on demand, occupancy, and time</li>
              <li>Accept online bookings from customers</li>
              <li>Process payments via Stripe and manage payouts</li>
              <li>Track bookings, customers, and analytics through an admin dashboard</li>
              <li>Manage multiple outlets/locations</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              The Service enables End-User Customers to browse listings, select time slots, and make bookings — with or without online payment.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">3. Account Registration</h2>
            <h3 className="text-lg font-medium mt-6 mb-3">3.1 Eligibility</h3>
            <p className="text-muted-foreground">
              You must be at least 18 years old and have the legal authority to enter into these Terms to create a Business User account. By registering, you represent that you meet these requirements.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.2 Account Security</h3>
            <p className="text-muted-foreground">You are responsible for:</p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorised use of your account</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We support email/password authentication (with strong password requirements) and Google OAuth sign-in. You must use a valid email address and verify it before accessing the admin dashboard.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">3.3 Account Termination</h3>
            <p className="text-muted-foreground">
              We reserve the right to suspend or terminate your account at our discretion if you violate these Terms, engage in fraudulent activity, or misuse the Service.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">4. Subscriptions and Billing</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">4.1 Plans and Pricing</h3>
            <p className="text-muted-foreground">
              Dibs offers tiered subscription plans (e.g., Starter, Growth, Enterprise) with varying features. Plan details and pricing are displayed during onboarding and in the admin dashboard. We reserve the right to modify pricing with reasonable notice.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">4.2 Free Trial</h3>
            <p className="text-muted-foreground">
              Eligible new accounts receive a 14-day trial of the Premium plan. No payment details are collected during onboarding and the trial does not automatically convert into a paid subscription. To continue using Dibs after the trial, you must choose a paid plan and complete payment through Stripe.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">4.3 Payment Processing</h3>
            <p className="text-muted-foreground">
              All subscription payments are processed through <strong className="text-foreground">Stripe</strong>. By subscribing, you agree to Stripe's <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms of Service</a>. We do not store your payment card details on our servers.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">4.4 Cancellation and Refunds</h3>
            <p className="text-muted-foreground">
              You may cancel your subscription at any time through the admin dashboard. Cancellation takes effect at the end of the current billing period. Refunds are handled on a case-by-case basis — please contact us at <a href="mailto:hello@usedibs.com" className="text-primary hover:underline">hello@usedibs.com</a>.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">5. Bookings and Payments</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">5.1 Booking Process</h3>
            <p className="text-muted-foreground">
              End-User Customers can make bookings through Dibs-powered listing pages. Bookings are subject to availability and the capacity limits set by the Business User. Customers must provide valid contact information (name and email) when booking.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">5.2 Dynamic Pricing</h3>
            <p className="text-muted-foreground">
              Prices for listings may vary based on demand, occupancy levels, time of day, and other factors configured by the Business User. The price displayed at the time of booking is the price the customer will be charged.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">5.3 Customer Payments</h3>
            <p className="text-muted-foreground">
              Customers may pay via Stripe (online payment) or manual payment methods (e.g., pay at venue), depending on the Business User's configuration. Online payments are processed via Stripe Checkout and are subject to Stripe's terms.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">5.4 Modifications and Cancellations</h3>
            <p className="text-muted-foreground">
              Customers may modify or cancel bookings through the links provided in confirmation emails. Modification and cancellation policies are set by the Business User and displayed in each listing's terms and conditions. Refunds for cancelled bookings paid via Stripe are processed back through Stripe.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">5.5 Business Payouts</h3>
            <p className="text-muted-foreground">
              Revenue from customer payments (minus applicable fees) is paid out to Business Users via Stripe Connect. Payout schedules and details are visible in the admin dashboard.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">6. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree not to:</p>
            <ul className="text-muted-foreground ml-4 list-disc space-y-2">
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Interfere with or disrupt the Service, servers, or networks</li>
              <li>Attempt to gain unauthorised access to any part of the Service</li>
              <li>Use automated tools (bots, scrapers) to access the Service without our written consent</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service to send spam, unsolicited messages, or engage in deceptive practices</li>
              <li>Upload malicious code, viruses, or any harmful content</li>
            </ul>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">7. Intellectual Property</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">7.1 Our Rights</h3>
            <p className="text-muted-foreground">
              The Service, including its design, features, code, branding, logos, and documentation, is owned by Dibs Everything Pte Ltd and protected by intellectual property laws. You may not copy, modify, distribute, or reverse-engineer any part of the Service without our written consent.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">7.2 Your Content</h3>
            <p className="text-muted-foreground">
              You retain ownership of the content you upload to the Service (e.g., listing descriptions, images, business information). By uploading content, you grant us a non-exclusive, worldwide, royalty-free licence to use, display, and distribute your content solely for the purpose of operating the Service.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">7.3 Feedback</h3>
            <p className="text-muted-foreground">
              Any feedback, suggestions, or ideas you provide to us may be used by us without obligation or compensation to you.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">8. Disclaimers</h2>
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4">
              <p className="text-sm text-muted-foreground">
                The Service is provided on an <strong className="text-foreground">"as is"</strong> and <strong className="text-foreground">"as available"</strong> basis. We make no warranties, express or implied, regarding the Service, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                We do not guarantee that the Service will be uninterrupted, error-free, or secure. We are not responsible for any loss of data, revenue, or business arising from use of the Service.
              </p>
            </div>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by applicable law, Dibs Everything Pte Ltd and its directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities.
            </p>
            <p className="text-muted-foreground mt-3">
              Our total aggregate liability for any claims arising out of or related to the Service shall not exceed the total fees paid by you to us in the twelve (12) months preceding the event giving rise to the claim.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">10. Indemnification</h2>
            <p className="text-muted-foreground">
              You agree to indemnify, defend, and hold harmless Dibs Everything Pte Ltd and its directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from:
            </p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of a third party</li>
              <li>Any content you upload to the Service</li>
            </ul>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">11. Relationship Between Business Users and Customers</h2>
            <p className="text-muted-foreground">
              Dibs acts as a technology platform connecting Business Users with their customers. We are <strong className="text-foreground">not</strong> a party to the transactions between Business Users and their customers. Business Users are solely responsible for:
            </p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>The quality, legality, and delivery of their services</li>
              <li>Setting accurate prices, descriptions, and terms and conditions for their listings</li>
              <li>Complying with all applicable laws and regulations in their jurisdiction</li>
              <li>Handling customer complaints, disputes, and refunds where manual payments are involved</li>
            </ul>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">12. Third-Party Services</h2>
            <p className="text-muted-foreground">
              The Service integrates with third-party services including Stripe, Google, Resend, and Vercel. Your use of these third-party services is subject to their respective terms and policies. We are not responsible for the availability, accuracy, or practices of third-party services.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">13. Privacy</h2>
            <p className="text-muted-foreground">
              Your use of the Service is also governed by our{" "}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and protect your personal data.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">14. Modifications to the Terms</h2>
            <p className="text-muted-foreground">
              We may update these Terms from time to time. When we make material changes, we will update the "Last updated" date at the top of this page and notify Business Users via email or through the admin dashboard. Your continued use of the Service after any changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">15. Governing Law and Disputes</h2>
            <p className="text-muted-foreground">
              These Terms are governed by and construed in accordance with the laws of the <strong className="text-foreground">Republic of Singapore</strong>. Any disputes arising from or relating to these Terms or the Service shall be submitted to the exclusive jurisdiction of the courts of Singapore.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">16. Severability</h2>
            <p className="text-muted-foreground">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">17. Contact Us</h2>
            <p className="text-muted-foreground mb-4">If you have any questions about these Terms, please contact us at:</p>
            <div className="rounded-lg border p-5">
              <p className="font-semibold text-foreground">Dibs Everything Pte Ltd</p>
              <p className="text-sm text-muted-foreground mt-1">📧 hello@usedibs.com</p>
              <p className="text-sm text-muted-foreground">📍 Singapore</p>
            </div>
          </section>
        </div>

        <p className="text-xs text-muted-foreground/70 italic mt-12 text-center">This policy is general information and not legal advice.</p>
      </article>
    </div>
  );
};

export default TermsOfService;
